/** Shared parsing primitives for the website-template validators. */
import { WEBSITE_RETIRED_SLOT_SEMANTICS } from '../../types/website-templates';

export type UnknownRecord = Record<string, unknown>;

export const WEBSITE_UUID_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
export const WEBSITE_COLLECTION_REF_RE = /^pc_[a-z0-9]{16,64}$/;
export const WEBSITE_SLOT_ID_RE = /^[a-z][a-zA-Z0-9]*(\.[a-zA-Z0-9-]+){1,6}$/;
export const WEBSITE_SLOT_ID_MAX_LENGTH = 96;
export const WEBSITE_BLOCK_ID_RE = /^[a-z][a-zA-Z0-9]{0,39}$/;
export const WEBSITE_VARIANT_RE = /^[a-z][a-z0-9-]{0,39}$/;
export const WEBSITE_TEMPLATE_ASSET_KEY_RE =
  /^[a-z0-9][a-z0-9-]*(\/[a-z0-9][a-z0-9-]*)*\.(webp|jpg|png)$/;
export const WEBSITE_TEMPLATE_ASSET_KEY_MAX_LENGTH = 160;

export const WEBSITE_DOCUMENT_MAX_BYTES = 256 * 1024;
export const WEBSITE_MAX_SLOTS = 300;

/**
 * Keys that describe structure, presentation code or raw destinations. A
 * merchant-writable payload carrying any of them is a structure override, not
 * a malformed value. Mirrored by SQL `website_is_structural_key`.
 */
export const WEBSITE_STRUCTURAL_KEYS = [
  'sections',
  'blocks',
  'pages',
  'layout',
  'component',
  'variant',
  'order',
  'css',
  'html',
  'script',
  'style',
  'className',
  'href',
  'slotIds',
  'chrome',
  'dataSource',
  'emptyBehavior',
  'layoutFamily',
  'src',
  'url',
] as const;

const STRUCTURAL_KEY_SET: ReadonlySet<string> = new Set(WEBSITE_STRUCTURAL_KEYS);

export { WEBSITE_RETIRED_SLOT_SEMANTICS };

/** Epic #228 P2: whether a slot semantic is retired (see the list's comment). */
const RETIRED_SEMANTIC_SET: ReadonlySet<string> = new Set(WEBSITE_RETIRED_SLOT_SEMANTICS);

export function isRetiredWebsiteSlotSemantic(semantic: string | null | undefined): boolean {
  return typeof semantic === 'string' && RETIRED_SEMANTIC_SET.has(semantic);
}

export function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function isStructuralKey(key: string): boolean {
  return STRUCTURAL_KEY_SET.has(key);
}

export type ClosedKeyIssue = 'structure_override' | 'invalid_slot';

/**
 * Closed-key check. Returns null when `value` has only `allowed` keys and all
 * `required` keys; otherwise classifies the first offending key.
 */
export function checkClosedKeys(
  value: UnknownRecord,
  allowed: readonly string[],
  required: readonly string[] = allowed,
): ClosedKeyIssue | null {
  let issue: ClosedKeyIssue | null = null;
  for (const key of Object.keys(value)) {
    if (allowed.includes(key)) continue;
    if (isStructuralKey(key)) return 'structure_override';
    issue = 'invalid_slot';
  }
  if (issue) return issue;
  for (const key of required) {
    if (!Object.prototype.hasOwnProperty.call(value, key)) return 'invalid_slot';
  }
  return null;
}

/**
 * Canonical JSON of a slot value or form state: key order never makes two
 * values differ, array order does, and `undefined` properties are absent.
 * One implementation for the region form diff and the save queue's equality.
 */
export function stableSlotJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableSlotJson).join(',')}]`;
  if (isRecord(value)) {
    return `{${Object.keys(value)
      .filter((key) => value[key] !== undefined)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableSlotJson(value[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

export function utf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

export function isOneOf<T extends string>(values: readonly T[], value: unknown): value is T {
  return typeof value === 'string' && (values as readonly string[]).includes(value);
}
